import { animate, AnimationTriggerMetadata, style, transition, trigger } from '@angular/animations';
import { Params } from '@angular/router';
import { DotNestedKeys } from './dot-nested-key.type';

export function formatByteSize(bytes: number): string {
    if (bytes === 0) {
        return '0 B';
    }
    const k: number = 1000;
    const dm: number = 3;
    const sizes: string[] = [ 'B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB' ];
    const i: number = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[ i ]}`;
}

export function isNullOrUndefined(_obj: any): boolean {
    return typeof _obj === 'undefined' || _obj == null;
}

export function deepCopy<T = any>(obj: T): T {
    if (!obj) {
        return obj;
    }

    return JSON.parse(JSON.stringify(obj));
}

export function equals(_obj1: any, _obj2: any): boolean {
    if (_obj1 === _obj2) {
        return true;
    }
    if (_obj1 === null || _obj2 === null) {
        return false;
    }
    if (_obj1 !== _obj1 && _obj2 !== _obj2) {
        return true; // NaN === NaN
    }
    const t1: any = typeof _obj1;
    const t2: any = typeof _obj2;
    let length: number;
    let key: any;
    let keySet: any;

    // eslint-disable-next-line eqeqeq
    if (t1 == t2 && t1 == 'object') {
        if (Array.isArray(_obj1)) {
            if (!Array.isArray(_obj2)) {
                return false;
            }
            // eslint-disable-next-line eqeqeq
            if ((length = _obj1.length) == _obj2.length) {
                for (key = 0; key < length; key++) {
                    if (!equals(_obj1[ key ], _obj2[ key ])) {
                        return false;
                    }
                }
                return true;
            }
        } else {
            if (Array.isArray(_obj2)) {
                return false;
            }
            keySet = Object.create(null);
            for (key in _obj1) {
                if (!equals(_obj1[ key ], _obj2[ key ])) {
                    return false;
                }
                keySet[ key ] = true;
            }
            for (key in _obj2) {
                if (!(key in keySet) && typeof _obj2[ key ] !== 'undefined') {
                    return false;
                }
            }
            return true;
        }
    }
    return false;
}

export function toggleFullscreen(): void {
    const doc: any = document as any;
    if (!doc.fullscreenElement) {
        doc.documentElement.requestFullscreen();
    } else {
        if (doc.exitFullscreen) {
            doc.exitFullscreen();
        }
    }
}

export function round(value: number, decimals: number): number {
    return Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);
}

export function timeout(millis: number): Promise<void> {
    const promise: Promise<void> = new Promise((resolve: () => void): void => {
        setTimeout(() => {
            resolve();
        }, millis);
    });
    return promise;
}

export function validateCheckDigit(value: string): boolean {
    const checkDigit: string = value?.slice(-1);
    return checkDigit === calculateCheckDigit(value?.slice(0, -1));
}

export function calculateCheckDigit(value: string): string {
    const values: number[] = value?.split('').map((val: string) => +val).reverse();
    const sum: number = values?.reduce((prev: number, curr: number, i: number) => prev + curr * (i % 2 === 0 ? 3 : 1), 0);
    const next: number = Math.ceil(sum / 10) * 10;
    const rest: number = next - sum;
    return rest.toString();
}

export function convertParamsToQuery(params: Params, dasherize: boolean = true): string {
    if (!params) {
        return '';
    }

    const filteredParams: string = Object.keys(params)
        .filter((val: string) => params[ val ] !== undefined)
        .map((key: string) => ({ key, value: params[ key ] }))
        .map((data: { key: string, value: string }) => `${dasherize
            ? data.key.replace(/[A-Z]/g, (m: string) => `-${m.toLowerCase()}`)
            : data.key}=${data.value}`,
        )
        .join('&');

    if (!filteredParams) {
        return '';
    }

    return `?${filteredParams}`;
}

export class CoreAnimations {
    /** [@showHideRow] */
    public static tableRowAnimation: AnimationTriggerMetadata = trigger('showHideRow', [
        transition(':enter', [
            style({ opacity: '0' }),
            animate('0.2s', style({ opacity: '1' })),
        ]),
        transition(':leave', [
            style({ opacity: '1' }),
            animate('0.2s', style({ opacity: '0' })),
        ]),
    ]);
    /** [@showHideFooter] */
    public static tableFooterAnimation: AnimationTriggerMetadata = trigger('showHideFooter', [
        transition(':enter', [
            style({ display: 'none', opacity: '0' }),
            animate('0.2s', style({ display: 'initial', opacity: '1' })),
        ]),
        transition(':leave', [
            style({ opacity: '1' }),
            animate('0s', style({ opacity: '0' })),
        ]),
    ]);

}

export function nestedData<T>(data: T, field: DotNestedKeys<T>): unknown {
    if (!field) {
        return data;
    }

    return (field as string).split('.')?.reduce((prev: unknown, curr: string) => prev ? prev[ curr ] : prev, data);
}

export function some<T, D>(
    val: T,
    field: DotNestedKeys<T>,
    findValues: D[],
    compare: 'equals' | 'not-equals' | 'smaller' | 'bigger' | 'smaller-equals' | 'bigger-equals' = 'equals',
): boolean {
    if (compare === 'equals' || compare === 'not-equals') {
        return findValues.some((findValue: unknown) => compare === 'equals' ? nestedData(val, field) === findValue : nestedData(val, field) !== findValue);
    } else if (compare === 'smaller' || compare === 'bigger') {
        return findValues.some((findValue: unknown) => compare === 'smaller' ? nestedData(val, field) < findValue : nestedData(val, field) > findValue);
    } else if (compare === 'smaller-equals' || compare === 'bigger-equals') {
        return findValues.some((findValue: unknown) => compare === 'smaller-equals' ? nestedData(val, field) <= findValue : nestedData(val, field) >= findValue);
    }
    return false;
}
